/**
 * ChatPage.jsx — Page de chat en plein écran avec une agence
 *
 * Accessible via /chat/:agencySlug ou depuis VehicleDetail.
 * Crée ou récupère une discussion grâce à une clé de conversation unique,
 * affiche les messages groupés par date, et permet d'envoyer de nouveaux messages.
 * Des suggestions rapides sont affichées quand la conversation est vide.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiMessageCircle, FiRefreshCw, FiSend, FiZap } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import BottomNav from '../shared/BottomNav.jsx';
import '../home/Home.css';
import './ChatPage.css';

const apiBaseUrl = getApiBaseUrl();

/** Formate une date/heure en heure locale (ex: "14:30") */
function formatTime(value) {
	return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Retourne "Aujourd'hui", "Hier" ou la date complète selon l'ancienneté */
function formatDate(value) {
	const d = new Date(value);
	const today = new Date();
	if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
	const yesterday = new Date(today);
	yesterday.setDate(today.getDate() - 1);
	if (d.toDateString() === yesterday.toDateString()) return 'Hier';
	return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' });
}

/** Lit l'utilisateur connecté depuis localStorage (authUser en priorité, sinon profil) */
function getCurrentUser() {
	const authRaw = window.localStorage.getItem('exp-auth-user');
	const profileRaw = window.localStorage.getItem('exp-user-profile');
	const user = (authRaw ? JSON.parse(authRaw) : null) || (profileRaw ? JSON.parse(profileRaw) : null);
	const firstName = user?.firstName || user?.prenom || '';
	const lastName = user?.lastName || user?.nom || '';
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
	return {
		id: user?.id || user?._id || null,
		name: [firstName, lastName].filter(Boolean).join(' ').trim() || user?.email || 'Client',
		role: user?.role || 'user',
		photoUrl: user?.profilePhotoUrl || '',
		initials
	};
}

function ChatPage() {
	const location = useLocation();
	const params = useParams();
	const bottomRef = useRef(null);
	const textareaRef = useRef(null);

	// Utilisateur courant (ne change pas pendant la session)
	const currentUser = useMemo(() => getCurrentUser(), []);

	// Récupère les paramètres de la discussion depuis l'état de navigation ou l'URL
	const chatState = location.state || {};
	const queryParams = new URLSearchParams(location.search);
	const contextType  = chatState.contextType  || queryParams.get('mode')    || 'agency';
	const listingId    = chatState.listingId    || params.agencySlug          || queryParams.get('listing') || '';
	const vehicleName  = chatState.vehicleName  || queryParams.get('vehicle') || 'Annonce';
	const agencyName   = chatState.agencyName   || decodeURIComponent(params.agencySlug || '').replace(/-/g, ' ') || 'Agence partenaire';
	const agencyId     = chatState.agencyId     || queryParams.get('agencyId') || '';
	const agencyKey    = agencyId || agencyName;

	// Clé unique pour identifier la conversation (retrouve le même fil à chaque ouverture)
	const conversationKey = chatState.conversationKey || `${contextType}|${String(listingId || vehicleName).toLowerCase()}|${String(agencyKey).toLowerCase()}|${String(currentUser.id || currentUser.name).toLowerCase()}`;

	// ── État ────────────────────────────────────────────────────────────────
	const [discussion, setDiscussion] = useState(null);
	const [messages, setMessages] = useState([]);
	const [draft, setDraft] = useState('');
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	// refreshToken : incrémenté pour forcer un rechargement de la discussion
	const [refreshToken, setRefreshToken] = useState(0);

	// Messages suggérés pour démarrer la conversation
	const quickReplies = [
		'Le véhicule est-il encore disponible ?',
		'Quel est le prix final avec les options ?',
		'Peut-on récupérer à l\'aéroport ?',
		'Quelles sont les conditions de caution ?',
	];

	// Scroll en haut au montage
	useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, []);

	/**
	 * Chargement (ou création) de la discussion.
	 * L'API POST /api/discussions est idempotente : si la clé existe déjà,
	 * elle retourne la discussion existante avec ses messages.
	 * AbortController permet d'annuler la requête si le composant est démonté.
	 */
	useEffect(() => {
		const controller = new AbortController();
		const load = async () => {
			setLoading(true);
			setError('');
			try {
				const res = await fetch(`${apiBaseUrl}/api/discussions`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					signal: controller.signal,
					body: JSON.stringify({
						conversationKey,
						contextType,
						listingId,
						vehicleName,
						agencyId,
						agencyName,
						customerId: currentUser.id,
						customerName: currentUser.name
					})
				});
				const data = await res.json();
				if (!res.ok) throw new Error(data.message || 'Impossible de charger la discussion.');
				setDiscussion(data);
				setMessages(data.messages || []);
			} catch (e) {
				if (e.name !== 'AbortError') setError(e.message);
			} finally {
				if (!controller.signal.aborted) setLoading(false);
			}
		};
		load();
		return () => controller.abort();
	}, [agencyId, agencyName, contextType, conversationKey, currentUser.id, currentUser.name, listingId, refreshToken, vehicleName]);

	// Auto-scroll vers le bas à chaque nouveau message
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [messages]);

	// Polling toutes les 4s : récupère les nouveaux messages sans recharger toute la page
	useEffect(() => {
		if (!discussion?._id) return;
		const poll = async () => {
			try {
				const res = await fetch(`${apiBaseUrl}/api/discussions/${discussion._id}`);
				if (!res.ok) return;
				const data = await res.json();
				setMessages((prev) => {
					const newLen = data?.messages?.length ?? 0;
					return newLen > prev.length ? (data.messages || prev) : prev;
				});
			} catch { /* réseau indisponible, on réessaie au prochain tick */ }
		};
		const id = setInterval(poll, 4000);
		return () => clearInterval(id);
	}, [discussion?._id]);

	/**
	 * handleSend — Envoie un message textuel.
	 * Accepte un texte passé directement (suggestions rapides) ou le brouillon actuel.
	 */
	const handleSend = async (textOverride) => {
		const text = String(textOverride || draft).trim();
		if (!text || !discussion?._id) return;
		setSending(true);
		setError('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/discussions/${discussion._id}/messages`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					text,
					// Détermine le rôle de l'expéditeur selon son profil
					senderRole: currentUser.role === 'agency' ? 'agency' : contextType === 'owner' ? 'owner' : 'customer',
					senderName: currentUser.name,
					senderId: currentUser.id
				})
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.message || 'Envoi impossible.');
			setDiscussion(data);
			setMessages(data.messages || []);
			setDraft('');
			textareaRef.current?.focus();
		} catch (e) {
			setError(e.message);
		} finally {
			setSending(false);
		}
	};

	// Entrée = envoyer, Maj+Entrée = saut de ligne
	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	// Initiales de l'agence pour l'avatar (ex: "AutoMaroc Premium" → "AM")
	const agencyInitials = agencyName.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

	/**
	 * groupedMessages — Insère des séparateurs de date entre les messages.
	 * Produit un tableau mixte : { type: 'date', label } ou { type: 'msg', msg }.
	 */
	const groupedMessages = useMemo(() => {
		const groups = [];
		let lastDate = null;
		for (const msg of messages) {
			const d = msg.createdAt ? new Date(msg.createdAt).toDateString() : 'now';
			if (d !== lastDate) {
				groups.push({ type: 'date', label: msg.createdAt ? formatDate(msg.createdAt) : '' });
				lastDate = d;
			}
			groups.push({ type: 'msg', msg });
		}
		return groups;
	}, [messages]);

	if (loading) {
		return (
			<div className="cp-page">
				<nav className="cp-nav">
					<Link to="/" className="vx-brand">
						<img src="/assets/images/logo.png" alt="UltimateXDrive" width="36" height="36" style={{ objectFit: 'contain' }} />
						<span className="vx-brand-name">UltimateXDrive</span>
					</Link>
					<Link to={-1} className="cp-nav-back"><FiArrowLeft size={15} /> Retour</Link>
				</nav>
				<div className="cp-loading">
					<div className="cp-loading-dot" /><div className="cp-loading-dot" /><div className="cp-loading-dot" />
				</div>
			</div>
		);
	}

	return (
		<div className="cp-page">

			{/* ── Barre de navigation ── */}
			<nav className="cp-nav">
				<Link to="/" className="vx-brand">
					<img src="/assets/images/logo.png" alt="UltimateXDrive" width="36" height="36" style={{ objectFit: 'contain' }} />
					<span className="vx-brand-name">UltimateXDrive</span>
				</Link>
				<div className="cp-nav-center">
					<div className="cp-nav-avatar">{agencyInitials}</div>
					<div>
						<p className="cp-nav-name">{agencyName}</p>
						<p className="cp-nav-sub">{vehicleName}</p>
					</div>
				</div>
				<div className="cp-nav-actions">
					<button type="button" className="cp-icon-btn" onClick={() => setRefreshToken((v) => v + 1)} title="Actualiser">
						<FiRefreshCw size={16} />
					</button>
					<Link to={-1} className="cp-icon-btn" title="Retour"><FiArrowLeft size={16} /></Link>
				</div>
			</nav>

			<div className="cp-layout">

				{/* ── Sidebar informations ── */}
				<aside className="cp-sidebar">
					<div className="cp-sidebar-vehicle">
						<span className="cp-badge"><FiMessageCircle size={12} /> {contextType === 'owner' ? 'Propriétaire' : 'Agence'}</span>
						<h2 className="cp-sidebar-title">{vehicleName}</h2>
						<p className="cp-sidebar-agency">{agencyName}</p>
					</div>

					<div className="cp-sidebar-info">
						<div className="cp-info-row">
							<span>Votre nom</span>
							<strong>{currentUser.name}</strong>
						</div>
						<div className="cp-info-row">
							<span>Type</span>
							<strong>{contextType === 'owner' ? 'Propriétaire' : 'Agence'}</strong>
						</div>
						<div className="cp-info-row">
							<span>Messages</span>
							<strong>{messages.length}</strong>
						</div>
					</div>

					<div className="cp-sidebar-trust">
						<div className="cp-trust-item"><FiCheckCircle size={13} /> Réseau sécurisé</div>
						<div className="cp-trust-item"><FiCheckCircle size={13} /> Données chiffrées</div>
						<div className="cp-trust-item"><FiCheckCircle size={13} /> Réponse rapide</div>
					</div>
				</aside>

				{/* ── Zone de chat ── */}
				<main className="cp-chat">

					{/* Liste des messages (avec séparateurs de date) */}
					<div className="cp-messages">
						{groupedMessages.map((item, i) => {
							if (item.type === 'date') {
								return <div key={`d-${i}`} className="cp-date-divider"><span>{item.label}</span></div>;
							}
							const { msg } = item;
							const isSystem = msg.senderRole === 'system';
							// isMine : compare l'ID expéditeur si disponible, sinon compare le rôle
							const isMine = msg.senderId && currentUser.id
								? String(msg.senderId) === String(currentUser.id)
								: msg.senderRole === (currentUser.role === 'agency' ? 'agency' : 'customer');
							const side = isSystem ? 'system' : isMine ? 'mine' : 'theirs';

							return (
								<div key={msg._id || `${msg.senderName}-${msg.createdAt}-${msg.text}`} className={`cp-row cp-row--${side}`}>
									{side === 'theirs' && (
										<div className="cp-avatar cp-avatar--agency">{agencyInitials}</div>
									)}
									{side === 'mine' && currentUser.photoUrl && (
										<div className="cp-avatar cp-avatar--user"><img src={currentUser.photoUrl} alt="" /></div>
									)}
									{side === 'mine' && !currentUser.photoUrl && (
										<div className="cp-avatar cp-avatar--user">{currentUser.initials}</div>
									)}
									<div className={`cp-bubble cp-bubble--${side}`}>
										{side !== 'system' && (
											<div className="cp-bubble-meta">
												<span className="cp-bubble-name">{side === 'mine' ? 'Vous' : msg.senderName}</span>
												<span className="cp-bubble-time">{msg.createdAt ? formatTime(msg.createdAt) : ''}</span>
											</div>
										)}
										<p>{msg.text}</p>
									</div>
								</div>
							);
						})}
						<div ref={bottomRef} />
					</div>

					{/* Suggestions rapides — affichées uniquement quand pas de vrais messages */}
					{messages.filter((m) => m.senderRole !== 'system').length === 0 && (
						<div className="cp-quick">
							<span className="cp-quick-label"><FiZap size={12} /> Réponses rapides</span>
							<div className="cp-quick-chips">
								{quickReplies.map((q) => (
									<button key={q} type="button" className="cp-chip" onClick={() => handleSend(q)}>
										{q}
									</button>
								))}
							</div>
						</div>
					)}

					{error && <p className="cp-error">{error}</p>}

					{/* Zone de saisie */}
					<form className="cp-composer" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
						<textarea
							ref={textareaRef}
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder="Écrivez votre message… (Entrée pour envoyer)"
							rows="3"
						/>
						<button type="submit" className="cp-send" disabled={sending || !draft.trim()}>
							<FiSend size={16} />
							<span className="cp-send-label">{sending ? 'Envoi…' : 'Envoyer'}</span>
						</button>
					</form>
				</main>
			</div>

			<BottomNav />
		</div>
	);
}

export default ChatPage;
